import React, { useState } from "react";
import DataTable from "../../Components/ui/dataTable";
import { Button } from "../../Components/ui/button";
import { DatePickerWithRange } from "../../Components/ui/dateRange";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../Components/ui/select";
import { Modal, ModalContent, ModalTrigger } from "../../Components/ui/modal";
import { SkeletonTable } from "../../Components/ui/skeleton";
import Pagination from "../../Components/ui/pagination";
import { ProtectedRoutes } from "../../config/protectedRoutes";
import { useDebouncedValue } from "../../Lib/hooks";
import { format, parseISO } from "date-fns";
import {
  useGetAllCustomersQuery,
  useGetCustomerStatsQuery,
} from "./CustomerApi";
import CustomerDetail from "./CustomerDetail";

const Customers = () => {
  const today = new Date();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [activated, setActivated] = useState("");
  const [date, setDate] = useState({ from: null, to: null });

  const debouncedSearch = useDebouncedValue(search, 500);

  const hasActiveFilters =
    search !== "" || activated !== "" || date.from !== null || date.to !== null;

  const clearFilters = () => {
    setSearch("");
    setActivated("");
    setDate({ from: null, to: null });
    setPage(1);
  };

  const { data, isLoading: loading } = useGetAllCustomersQuery(
    {
      page,
      name: debouncedSearch,
      activated,
      startDate: date?.from ? format(date.from, "yyyy-MM-dd") : "",
      endDate: date?.to ? format(date.to, "yyyy-MM-dd") : "",
    },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: stats } = useGetCustomerStatsQuery();

  const columns = [
    {
      accessorKey: "no",
      header: "No",
      cell: ({ row }) => <div className='w-[40px]'>{row.getValue("no")}</div>,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className='font-semibold whitespace-nowrap'>
          {row.getValue("name")}
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <div className='lowercase'>{row.getValue("email")}</div>
      ),
    },
    {
      accessorKey: "phoneNumber",
      header: "Phone",
      cell: ({ row }) => <div>{row.getValue("phoneNumber")}</div>,
    },

    {
      accessorKey: "emailVerified",
      header: "Email Verified",
      cell: ({ row }) => {
        const verified = row.getValue("emailVerified");
        return (
          <Button
            asChild
            variant={verified ? "successful" : "pending"}
            size='sm'
          >
            <p>{verified ? "Verified" : "Unverified"}</p>
          </Button>
        );
      },
    },
    {
      accessorKey: "activated",
      header: "Status",
      cell: ({ row }) => {
        const active = row.getValue("activated");
        return (
          <Button asChild variant={active ? "successful" : "failed"} size='sm'>
            <p>{active ? "Active" : "Inactive"}</p>
          </Button>
        );
      },
    },
    {
      id: "action",
      cell: ({ row }) => {
        const customerId = row.original.id;
        return (
          <Modal>
            <ModalContent name='Customer Details'>
              <CustomerDetail id={customerId} />
            </ModalContent>
            <ModalTrigger>
              <Button
                variant='outline'
                className='outline-none focus:outline-none focus-visible:outline-none px-4 whitespace-nowrap'
              >
                View Details
              </Button>
            </ModalTrigger>
          </Modal>
        );
      },
    },
  ];

  return (
    <div className='bg-white p-4'>
      <div className='mb-4'>
        <h2 className='text-2xl font-bold text-primaryBlack'>Customers</h2>
        <p className='text-sm text-[#667499]'>
          View and manage customer accounts
        </p>
      </div>
      {/* Stats */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
        <div className='bg-[#F5FEF1] rounded-md p-4 shadow-sm'>
          <p className='text-xs text-gray-500 font-medium'>Total Customers</p>
          <p className='text-2xl font-bold text-primaryBlack mt-1'>
            {stats?.data?.total ?? "—"}
          </p>
        </div>
        <div className='bg-[#F5FEF1] rounded-md p-4 shadow-sm'>
          <p className='text-xs text-gray-500 font-medium'>Activated</p>
          <p className='text-2xl font-bold text-[#163C29] mt-1'>
            {stats?.data?.activated ?? "—"}
          </p>
        </div>
        <div className='bg-[#FEF6CF] rounded-md p-4 shadow-sm'>
          <p className='text-xs text-gray-500 font-medium'>Deactivated</p>
          <p className='text-2xl font-bold text-[#6B2B0D] mt-1'>
            {stats?.data?.deactivated ?? "—"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap items-center gap-3 mb-4'>
        <div className='flex items-center bg-[#F5FEF1] w-full sm:w-[200px] p-1 rounded-sm border border-gray-100'>
          <input
            className='bg-transparent w-full px-1 outline-none placeholder:text-sm text-sm'
            value={search}
            placeholder='Search by name'
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select
          value={activated === "" ? "all" : activated}
          onValueChange={(val) => {
            setActivated(val === "all" ? "" : val);
            setPage(1);
          }}
        >
          <SelectTrigger className='w-[140px] text-sm'>
            <SelectValue placeholder='All Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            <SelectItem value='true'>Active</SelectItem>
            <SelectItem value='false'>Inactive</SelectItem>
          </SelectContent>
        </Select>

        <DatePickerWithRange date={date} setDate={setDate} />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className='text-sm text-red-500 hover:text-red-700 underline whitespace-nowrap'
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className='table-scroll max-h-[70vh] overflow-auto max-w-full rounded-md border'>
        <div className='w-full'>
          {loading ? (
            <SkeletonTable />
          ) : (
            <DataTable columns={columns} data={data?.data} />
          )}
        </div>
      </div>

      {!loading && (
        <Pagination
          page={page}
          limit={data?.limit ?? 0}
          total={data?.total ?? 0}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default ProtectedRoutes(Customers, [5, 2, 1]);
